<template lang='pug'>
#slideshow-counting-eigenvalues.eg-slideshow
  slide
    .title
      h1 Counting eigenvalues
      h2 Determining the index of an eigenvalue of an elliptic operator
    .name2
      | Toon Baeyens
      br
      a.email(href="mailto:toon.baeyens@ugent.be") toon.baeyens@ugent.be
    .name
      | Marnix Van Daele
      br
      a.email(href="mailto:marnix.vandaele@ugent.be") marnix.vandaele@ugent.be

  toc(:inserted="true")

  slide(:steps="2")
    h3 Elliptic self-adjoint operators
    p Let <mj m="\mathcal{L}"/> be an linear uniformly elliptic self-adjoint operator with homogeneous Dirichlet boundary conditions on a bounded Lipschitz domain <mj m="\Omega \subseteq \mathbb{R}^n"/>.
    .theorem(v-if="step == 1")
      p The spectrum of <mj m="\mathcal{L}" /> is real, has a lower bound, no upper bound, and is countable:
      mj(display) \lambda_0 \leq \lambda_1 \leq \lambda_2 \leq \lambda_3 \leq \dots \to +\infty
    .theorem(v-else-if="step == 2")
      p There exists an orthogonal Hilbert basis of <mj m="L^2_0(\Omega)" /> consisting of eigenfunctions <mj m="u_i"/> (with eigenvalue <mj m="\lambda_i"/>) of <mj m="\mathcal{L}" />.
      mj(display) \int_\Omega u_i u_j = 0 \quad\quad\text{ if $\lambda_i \neq \lambda_j$}
      ul.comment
        li Explain L_0^2

    slide_footer Elliptic operators

  slide(:steps="2")
    h3 Example: Fourier

    p Find eigenvalues <mj m="\lambda"/> and eigenfunctions <mj m="u : [0; \pi] \to \mathbb{R}"/> such that <mj m="u(0) = u(\pi) = 0"/> and
      mj(display) -\dv[2]{u}{x} = \lambda u\text{.}

    p(v-if="step == 2")
      | For each <mj m="k\in \{0, 1, 2, \dots\}"/>:
      mj(display) \lambda_k = (k+1)^2
      mj(display) u_k = \sin((k+1) x)

    slide_footer Elliptic operators
  slide(:steps="3")
    h3 Example: Standing waves on a drum

    mj(display) -\nabla^2 \psi = \lambda \psi \quad \quad \text{on $B(\vb{0}, 1)$}

    div(v-if="step == 1")
      examples_on_drum(
        :canvasScale="scale",
        style="width: 450px; height: 270px"
      )
      img.image-right(
        src="./assets/examples/on_drum_5.png",
        width="240px",
        style="right: 48px"
      )
    img.center(
      v-else-if="step == 2",
      src="./assets/examples/on_drum.png",
      width="640px"
    )
    img.center(v-else, src="./assets/examples/on_drum_bold.png", width="640px")

    slide_footer Elliptic operators
  slide(:steps="2")
    h3 Example: Hénon-Heiles potential

    mj(display, tight) -\nabla^2 u + \left(x^2 + y^2 + \frac{y}{6\sqrt{5}}(3x^2 - y^2)\right)u = \lambda u \quad\text{on $[-6;6]\times[-6;6]$}

    img(src="./assets/examples/henon_heiles.png", width="236px")

    img.image-right(
      v-if="step == 2",
      src="./assets/examples/henon_heiles_eigenfunctions.png",
      style="bottom: 40px",
      width="460px"
    )

    slide_footer Elliptic operators
  slide
    h3 Courant's min-max theorem

    ul.citations
      li Courant R, and Hilbert D. <i>Methods of Mathematical Physics (Vol I, Chapter VI, §2)</i>. John Wiley & Sons, 2008.

    p Let <mj m="\mathcal{L}"/> be an linear uniformly elliptic self-adjoint operator with homogeneous Dirichlet boundary conditions on a bounded Lipschitz domain <mj m="\Omega \subseteq \mathbb{R}^n"/>.

    .theorem
      p All eigenvalues can be, independently, identified with
        mj(display) \lambda_n = \min_{\psi_0, \dots, \psi_{n}}\max\left\{\bra{\psi}\ket{\mathcal{L}\psi}: \psi \in \span(\psi_0, \dots, \psi_n), \|\psi\| = 1 \right\}
        | with all <mj m="\psi_i"/> orthogonal and satisfying the boundary conditions.

    p.comment
      | Say: Rayleigh-coefficient

    slide_footer Courant's nodal domain theorem
  slide(:steps="2")
    h3 Consequences of the min-max theorem

    ul.citations
      li Courant R, and Hilbert D. <i>Methods of Mathematical Physics (Vol I, Chapter VI, §2)</i>. John Wiley & Sons, 2008.

    .theorem
      p For finitely many disjoint sets <mj m="G,G',G'',\dots\subseteq \Omega"/>, there are at least as many eigenvalues of <mj m="\mathcal{L}"/> not larger then <mj m="E" /> on <mj m="\Omega"/> as the total number of eigenvalues not larger then <mj m="E" /> on all subdomains.

    img.center(
      v-if="step == 2",
      src="./assets/courant_example/example.svg",
      width="620px"
    )

    slide_footer Courant's nodal domain theorem
  slide
    h3 Consequences of the min-max theorem

    ul.citations
      li Courant R, and Hilbert D. <i>Methods of Mathematical Physics (Vol I, Chapter VI, §2)</i>. John Wiley & Sons, 2008.

    p <i>Sketch of proof</i><br/> Define the <mj m="i^\text{th}"/> eigenfunction of <mj m="\mathcal{L}"/> on <mj m="G^{j}"/> as <mj m="v_i^{(j)}"/>, with eigenvalue <mj m="\lambda_i^{(j)}"/>. Assume there are <mj m="n"/> such eigenvalues for which <mj m="\lambda_i^{(j)} \leq E"/>. The corresponding <mj m="n"/> eigenfunctions (extended to <mj m="\Omega" />) form an orthogonal basis for <mj m="L_0^2(\Omega)"/>, so from the min-max theorem on <mj m="\Omega"/> we find:
    mj(display) \lambda_n \leq \max\left\{ \bra{\psi}\ket{\mathcal{L}\psi} : \psi \in \span(v_i^{(j)}), \|\psi\| = 1 \right\} \leq E\text{.}

    slide_footer Courant's nodal domain theorem
  slide(:steps="2")
    h3 Nodal domains

    ul.citations
      li Courant R, and Hilbert D. <i>Methods of Mathematical Physics (Vol I, Chapter VI, §2)</i>. John Wiley & Sons, 2008.

    p
      | A <i>nodal domain</i> of a function <mj m="u : \Omega \to \mathbb{R}"/> is a largest connected set of points <mj m="\vb{x}\in\Omega"/>, such <mj m="u(\vb{x}) \neq 0"/>.

    // u_12
    img.center(
      v-if="step == 2",
      src="./assets/nodal_domains/example.svg",
      width="450px"
    )

    slide_footer Courant's nodal domain theorem
  slide(:steps="2")
    h3 Courant's nodal domain theorem

    ul.citations
      li Courant R, and Hilbert D. <i>Methods of Mathematical Physics (Vol I, Chapter VI, §2)</i>. John Wiley & Sons, 2008.
    | <br />
    .theorem
      p The <mj m="i"/><sup>th</sup> eigenfunction has at most <mj m="i+1"/> nodal domains.

    p(v-if="step == 2")
      | <i>Proof</i><br />
      | The <mj m="k"/> nodal domains of the eigenfunction <mj m="u_i" /> are a set of disjoint subdomains. On each of these subdomains <mj m="\lambda_i" /> is also an eigenvalue. As a consequence of the previous theorem there are at least <mj m="k"/> eigenvalues less then or equal to <mj m="\lambda_i"/> on <mj m="\Omega"/>. Thus <mj m="k \leq i+1"/>.

    slide_footer Courant's nodal domain theorem
  slide(:steps="2")
    h3 Courant's nodal domain Theorem

    .theorem
      h4 Special case for one dimensional problems
      p
        | The <mj m="i"/><sup>th</sup> eigenfunction of the one dimensional Sturm-Liouville equation has exactly <mj m="i" /> zeros in the interior of its domain.

    p(v-if="step == 2", style="width: 300px; padding-top: 30px")
      | For shooting methods, the Prüfer transformation can be used to keep track of the zero-crossings.

    prufer_animation.image-right(
      width="420px",
      :active="step == 2",
      :offset="5"
    )

    slide_footer Courant's nodal domain theorem
  slide
    h3 Ixaru's 2D shooting method

    .theorem
      h4 Schrödinger equation
      p
        | For a given potential <mj m="V(x, y)"/>, find eigenvalues <mj m="\lambda"/> and eigenfunctions <mj m="u(x, y)"/> such
        mj(display) -\nabla^2 u + V(x,y) u = \lambda u
        | on the rectangle <mj m="[\xmin, \xmax]\times[\ymin, \ymax]"/>, with <mj m="u = 0"/> on the boundary.

    slide_footer Ixaru's 2D shooting method
  slide(:steps="2")
    h3 Ixaru's 2D shooting method

    p
      | Let <mj m="b_i(x)"/> be a well choosen basis along the <mj m="x"/>-axis. Denote
      mj(display) u(x, y) = \sum_{i=0}^{\infty} b_i(x) c_i(y) \text{.}

    div(v-if="step == 1")
      p
        | Some questions:
        ul
          li Which basis is optimal?
          li Is the basis easy to compute?
          li Can this basis vary along the domain?

      ul.comment
        li Say: What does 'optimal' mean?
          ul
            li Computers are finite, so should this basis be. Least amount of basisfunctions needed.
      ul.citations
        li Ixaru L. Gr. <i>New numerical method for the eigenvalue problem of the 2D Schrödinger equation</i>. Computer Physics Communications, 2010.

    div(v-else-if="step == 2")
      p
        | Let <mj m="b_i(x)"/> be the eigenfunctions of
        mj(display, tight) -\dv[2]{b_i}{x} + \overline{V}(x) b_i = \lambda_i b_i

      // ul
        li What should <mj m="\overline{V}(x)" /> be?
      p This can be very efficiently computed thanks to Matslise.
      ul.citations
        li Baeyens T., and Van Daele M. <i>The fast and accurate computation of eigenvalues and eigenfunctions of time-independent one-dimensional Schrödinger equations</i>. Computer Physics Communications, 2020.

      ul.comment
        li Say: Key insight from Ixaru.
        li Matslise in Ghent, Ixaru has SLCPM12

    slide_footer Ixaru's 2D shooting method
  slide
    h3 Ixaru's 2D shooting method

    img.image-right(src="./assets/ixaru/sectors.svg", width="330px")

    div(style="width: 380px")
      p
        | Denote for each sector <mj m="k"/>
        mj(display, :tight="[16, 20]") u(x, y) = \sum_{i=0}^{\infty} b^{(k)}_i(x) c_i^{(k)}(y)
        | for <mj m="x \in [\xmin, \xmax]"/> and <mj m="y \in [y_{k-1}, y_k]"/>.
      p
        | Let <mj m="b_i^{(k)}"/> be the eigenfunctions of
        mj(display, :tight="[16, 12]") -\dv[2]{x} b^{(k)}_i(x) + \overline{V}^{(k)}(x) = \lambda_i^{(k)} b_i^{(k)}
        | with <mj m="\overline{V}^{(k)}(x) = V\left(x, \frac{y_{k-1} + y_{k}}{2}\right)" />.

    ul.citations
      li Ixaru L. Gr. <i>New numerical method for the eigenvalue problem of the 2D Schrödinger equation</i>. Computer Physics Communications, 2010.

    slide_footer Ixaru's 2D shooting method
  slide
    h3 Ixaru's 2D shooting method

    p
      | To propagate solutions <b>within a sector</b> the following system of ODE's can be used, with <mj m="\vb{V}^{(k)}(y)"/> a specific matrix function.
      mj(display, tight) -\dv[2]{\vb{c}^{(k)}}{y} + \vb{V}^{(k)}(y) \vb{c}^{(k)} = E \vb{c}^{(k)}

      // mj(display) \vb{V}^{(k)}_{ij}(y) = \int_{\xmin}^{\xmax} \left(V(x, y) - \overline{V}^{(k)}(x)\right) b_i^{(k)}b_j^{(k)} \dd x + \delta_{ij} \lambda_i^{(k)}

      | To convert a solution <b>between sectors</b>, an orthogonal matrix <mj m="\vb{M}^{(k)}"/> can be computed.
      mj(display, tight)
        | \begin{align}
        | \vb{c}^{(k+1)}(y_k) &= \vb{M}^{(k)}\vb{c}^{(k)}(y_k) \\
        | \dv{\vb{c}^{(k+1)}}{y}(y_k) &= \vb{M}^{(k)}\dv{\vb{c}^{(k)}}{y}(y_k)
        | \end{align}

    ul.citations
      li Ixaru L. Gr. <i>New numerical method for the eigenvalue problem of the 2D Schrödinger equation</i>. Computer Physics Communications, 2010.

    slide_footer Ixaru's 2D shooting method
  slide(:steps="3")
    h3 Ixaru's 2D shooting method

    shooting(
      :canvasScale="scale",
      :step="step",
      :autoStartAnimation="false"
    )

    slide_footer Ixaru's 2D shooting method
  slide(:steps="3")
    h3 How to find all eigenvalues?

    .theorem
      img.center(
        v-if="step == 1",
        src="./assets/ixaru/coulomb/sector0.svg",
        width="580px"
      )
      img.center(
        v-else-if="step == 2",
        src="./assets/ixaru/coulomb/sector0_doubles.svg",
        width="580px"
      )
      img.center(
        v-else,
        src="./assets/ixaru/coulomb/sector0_missing.svg",
        width="580px"
      )

    .theorem
      img.center(
        v-if="step == 1",
        src="./assets/ixaru/coulomb/sector1.svg",
        width="580px"
      )
      img.center(
        v-else-if="step == 2",
        src="./assets/ixaru/coulomb/sector1_doubles.svg",
        width="580px"
      )
      img.center(
        v-else,
        src="./assets/ixaru/coulomb/sector1_missing.svg",
        width="580px"
      )

    slide_footer Ixaru's 2D shooting method
  slide
    h3 How to find all eigenvalues?

    div(style="padding-top: 40px")
      p Issues with Courant's nodal domain theorem:
      ul
        li It gives only a lower bound.
        li Counting nodal domains is a difficult task.
        li Counting nodal domains requires many eigenfunction evaluations.

    slide_footer Ixaru's 2D shooting method
  slide(:steps="3")
    h3 A new tool

    new_tool_counting(
      :index="1",
      :ymax="-1",
      :canvasScale="scale",
      :step="step",
      :autoStartAnimation="false"
    )

    slide_footer A new tool
  slide
    h3 A new tool

    img.image-right(
      src="./assets/new_tool/eigenvalues_graph.svg",
      width="380px",
      style="bottom: 50px"
    )

    div(style="width: 330px")
      p 
        | The first eigenvalues of the Schrödinger equation
        mj(display) -\nabla^2\psi_i + V(x, y)\psi_i = \lambda_i\psi_i
        | on <mj m="[-4; 4]\times[-4; \ymax]"/> with potential
        mj(display, tight) V(x, y) = (1+x^2)(1+y^2)
        | and homogeneous Dirichlet boundary conditions are plotted.

    slide_footer A new tool
  slide(:steps="2")
    h3 A new tool

    .theorem
      p
        | Let <mj>\left\{\Omega_\epsilon \subseteq \mathbb{R}^n | \forall \epsilon \in \left]0, 1\right]\right\}</mj> be a continuous set of diffeomorphic bounded Lipschitz domains, such that <mj>a < b \implies \Omega_a \subsetneq \Omega_b</mj><span v-if="step==1">. </span><span v-else>, </span>
        span(:class="{ 'color-white': step == 1, 'color-blue': step == 2 }") and
          mj(display, style="margin-top: -20px") \lim_{\epsilon \to 0^{+}} \sup_{\vb{x} \in \Omega_\epsilon} \meas_1 (S_{\Omega_\epsilon, n}(\vb{x})) = 0\text{.}

    img.center(
      v-if="step == 1",
      src="./assets/new_tool_omega.png",
      width="560"
    )
    img.center(v-else, src="./assets/new_tool_omega_vertical.png", width="560")

    ul.citations
      li Baeyens T., and Van Daele M. <i>Determining indices of eigenvalues of linear self-adjoint elliptic operators on bounded Lipschitz domains</i>. Submitted 2021.

    slide_footer A new tool
  slide
    h3 A new tool

    img.center(
      src="./assets/new_tool_omega.png",
      width="560",
      style="margin-bottom: 12px"
    )
    img.center(src="./assets/new_tool_rho.png", width="560")

    ul.citations
      li Baeyens T., and Van Daele M. <i>Determining indices of eigenvalues of linear self-adjoint elliptic operators on bounded Lipschitz domains</i>. Submitted 2021.

    slide_footer A new tool
  slide
    h3 A new tool

    .theorem
      h4 Theorem
      p
        | Let <mj>\left\{\Omega_\epsilon \subseteq \mathbb{R}^n | \forall \epsilon \in \left]0, 1\right]\right\}</mj> be a continuous set of diffeomorphic bounded Lipschitz domains, such that <mj>a < b \implies \Omega_a \subsetneq \Omega_b</mj>, and
        mj(display, style="margin: -16px 0 -16px 0") \lim_{\epsilon \to 0^{+}} \sup_{\vb{x} \in \Omega_\epsilon} \meas_1 (S_{\Omega_\epsilon, n}(\vb{x})) = 0\text{.}
      p
        | Let <mj>\lambda_{\epsilon,i}</mj> be the <mj>i^\text{th}</mj> eigenvalue of <mj>\mathcal{L}</mj> limited to <mj>\Omega_\epsilon</mj>. Then <mj>\forall i \in \mathbb{N}</mj>, the values <mj>\lambda_{\epsilon,i}</mj> as functions of <mj>\epsilon</mj>
        ul
          li are continuous,
          li are strictly decreasing,
          li and are unbounded for <mj>\epsilon \to 0^{+}</mj>.

    ul.citations
      li Baeyens T., and Van Daele M. <i>Determining indices of eigenvalues of linear self-adjoint elliptic operators on bounded Lipschitz domains</i>. Submitted 2021.

    slide_footer A new tool
  slide
    h3 A new tool

    p <i>Sketch of proof</i><br/>
      | Continuity and the strict decreasing property already proven by Courant.
    p To proof unboundedness, Gårding's inequality removes the dependence on the specific operator. A Friedrichs's-like inequality is used to estimate the Sobolev norm in function of the size of the domain.
      mj(display) \frac{\bra{\mathcal{L}u}\ket{u}_{L^2(\Omega_\epsilon)}}{\|u\|_{L^2(\Omega_\epsilon)}} + C_1 \geq C_2 \frac{\|u\|_{H^k(\Omega_\epsilon)}}{\|u\|_{L^2(\Omega_\epsilon)}} \geq \frac{C_3}{\sqrt{\sup_{x\in\Omega_\epsilon}\meas_1(S_{\Omega_\epsilon, n}(\vb{x}))}}

    ul.citations
      li Baeyens T., and Van Daele M. <i>Determining indices of eigenvalues of linear self-adjoint elliptic operators on bounded Lipschitz domains</i>. Submitted 2021.

    slide_footer A new tool
  slide(:steps="3")
    h3 A new tool: corollary

    div(style="width: 330px; padding-top: 30px")
      h4 How to use this theorem?
      p(v-if="step > 1")
        | For a fixed energy <mj m="E" />, while propagating count the number of domains for which <mj m="E" /> is an eigenvalue.

    img.image-right(
      v-if="step == 1",
      src="./assets/new_tool/eigenvalues_graph.svg",
      width="360px",
      style="bottom: 50px"
    )
    img.image-right(
      v-else-if="step == 2",
      src="./assets/new_tool/eigenvalues_graph_energy.svg",
      width="360px",
      style="bottom: 50px"
    )
    img.image-right(
      v-else-if="step == 3",
      src="./assets/new_tool/eigenvalues_graph_energy_crossings.svg",
      width="360px",
      style="bottom: 50px"
    )

    ul.citations
      li Baeyens T., and Van Daele M. <i>Determining indices of eigenvalues of linear self-adjoint elliptic operators on bounded Lipschitz domains</i>. Submitted 2021.

    slide_footer A new tool
  slide
    h3 A new tool: corollary

    h4 For a fixed energy <mj m="E"/>, how does one find subdomains on which <mj m="E"/> is an eigenvalue?

    p In Ixaru's 2D shooting method, one is keeping track of how many non-trivial solutions exist of
      mj(display, tight) \sum_{i} a_i \vb{c_i}^{(k)}(y) = \vb{0}
      | for <mj m="y"/> varying from <mj m="\ymin"/> to <mj m="\ymax"/>.

    p The Prüfer-Atkinson transformation, for a coupled system of Schrödinger equations, exactly keeps track of this property.

    slide_footer A new tool

  toc(:inserted="true", removeFirst)

  slide
    .title
    .name2
      | Toon Baeyens
      br
      a.email(href="mailto:toon.baeyens@ugent.be") toon.baeyens@ugent.be
    .name
      | Marnix Van Daele
      br
      a.email(href="mailto:marnix.vandaele@ugent.be") marnix.vandaele@ugent.be
</template>

<script>
import { Slideshow } from "eagle.js";
import TOC from "./components/TOC.vue";
import Footer from "./components/Footer.vue";
import MathJax from "./components/MathJax.vue";
import IxaruShooting from "./components/IxaruShooting.vue";
import ExamplesOnDrum from "./components/examples/OnDrum.vue";
import NewToolCounting from "./components/NewToolCounting.vue";
import PruferAnimation from "./components/PruferAnimation.vue";

export default {
  name: "App",
  mixins: [Slideshow],
  components: {
    slide_footer: Footer,
    mj: MathJax,
    shooting: IxaruShooting,
    toc: TOC,
    examples_on_drum: ExamplesOnDrum,
    new_tool_counting: NewToolCounting,
    prufer_animation: PruferAnimation,
  },
  data() {
    return { scale: 1 };
  },
  mounted() {
    this.updateSlides();
  },
  methods: {
    handleResize() {
      // Called from eagle.slideshow
      const scale = Math.min(window.innerWidth / 800, window.innerHeight / 450);
      this.scale = scale;
      document.documentElement.style.setProperty("--screen-scale", scale);
    },
    updateSlides: function () {
      this.currentSlideIndex = +this.$route.params.slide || 1;
      this.$nextTick(() => {
        this.step = +this.$route.params.step || 1;
      });
    },
    updateURL: function () {
      this.$router
        .push(`/${this.currentSlideIndex}/${this.step}`)
        .catch((error) => {
          if (error.name != "NavigationDuplicated") throw error;
        });
    },
  },
  watch: {
    $route: "updateSlides",
    step: "updateURL",
    currentSlideIndex: "updateURL",
  },
  computed: {
    slideCounter() {
      return `${this.currentSlideIndex}/${this.slides.length}`;
    },
  },
};
</script>

<style>
</style>
